/**
 * Навігація «назад» для PWA (standalone без кнопок браузера).
 * Пріоритет: location.state.returnTo → карта батьківських шляхів → головна.
 */

/** Повернення на вкладку складу. */
export function stockReturnTo(tab = 'raw') {
  const t = String(tab || 'raw').replace(/_/g, '-');
  return t === 'raw' ? '/stock' : `/stock?tab=${encodeURIComponent(t)}`;
}

export function stockReturnLabel(tab = 'raw') {
  const labels = {
    raw: 'До складу сировини',
    dyes: 'До складу барвників',
    packaging: 'До складу упаковки',
    technical: 'До складу техматеріалів',
    semi: 'До напівфабрикатів',
    'packaging-work': 'До складу для пакування',
    packaging_work: 'До складу для пакування',
    finished: 'До готової продукції',
    'crushing-waste': 'До складу перемолів',
    crushing_waste: 'До складу перемолів',
    blocking: 'До блокування',
    consumable: 'До розхідних',
    defect: 'До браку',
    samples: 'До взірців',
    movements: 'До журналу рухів',
  };
  return labels[tab] || 'До складів';
}

/** navigate(path) зі збереженням куди повертатись. */
export function navigateWithReturn(navigate, path, returnTo, returnLabel = 'Назад') {
  if (!navigate) return;
  if (returnTo) {
    navigate(path, { state: { returnTo, returnLabel } });
  } else {
    navigate(path);
  }
}

const PARENT_RULES = [
  [/^\/raw-materials$/, '/stock?tab=raw', 'До складу сировини'],
  [/^\/dyes$/, '/stock?tab=dyes', 'До складу барвників'],
  [/^\/packaging-materials$/, '/stock?tab=packaging', 'До складу упаковки'],
  [/^\/filament$/, '/stock', 'До складів'],
  [/^\/consumable-materials$/, '/stock?tab=consumable', 'До складу розхідних'],
  [/^\/products$/, '/production', 'До виробництва'],
  [/^\/machines$/, '/production', 'До виробництва'],
  [/^\/press-forms$/, '/production', 'До виробництва'],
  [/^\/product-kits$/, '/production', 'До виробництва'],
  [/^\/customers\/[^/]+$/, '/customer-orders?tab=customers', 'До замовників'],
  [/^\/customers$/, '/customer-orders?tab=customers', 'До замовників'],
  [/^\/suppliers\/[^/]+$/, '/suppliers', 'До постачальників'],
  [/^\/suppliers$/, '/purchases', 'До закупівель'],
  [/^\/supplier-catalog$/, '/purchases', 'До закупівель'],
  [/^\/purchase-approvals$/, '/purchases', 'До закупівель'],
  [/^\/sample-warehouse$/, '/stock?tab=samples', 'До взірців'],
  [/^\/production-warehouse$/, '/stock', 'До складів'],
  [/^\/qc-stock-page$/, '/production', 'До виробництва'],
  [/^\/production-mixing$/, '/production', 'До виробництва'],
  [/^\/production-operator-v2$/, '/production', 'До виробництва'],
  [/^\/production-operator$/, '/production', 'До виробництва'],
  [/^\/production-drying$/, '/production', 'До виробництва'],
  [/^\/setup$/, '/production', 'До виробництва'],
  [/^\/crushing$/, '/production', 'До виробництва'],
  [/^\/planner-v2$/, '/production', 'До виробництва'],
  [/^\/shift-floor$/, '/production', 'До виробництва'],
  [/^\/qc$/, '/production', 'До виробництва'],
  [/^\/qc-kanban$/, '/production', 'До виробництва'],
  [/^\/maintenance$/, '/production', 'До виробництва'],
  [/^\/maintenance-plan$/, '/production', 'До виробництва'],
  [/^\/work-processes$/, '/production', 'До виробництва'],
  [/^\/packaging$/, '/production', 'До виробництва'],
  [/^\/packaging-daily-norm$/, '/packaging', 'До пакування'],
  [/^\/leads$/, '/customer-orders', 'До замовлень'],
  [/^\/projects$/, '/customer-orders', 'До замовлень'],
  [/^\/rnd$/, '/customer-orders', 'До замовлень'],
  [/^\/settings-.+$/, '/', 'На головну'],
  [/^\/notification-rules$/, '/', 'На головну'],
  [/^\/activity-log$/, '/', 'На головну'],
  [/^\/process-log$/, '/', 'На головну'],
  [/^\/bulk-edit$/, '/', 'На головну'],
  [/^\/export$/, '/', 'На головну'],
  [/^\/import$/, '/', 'На головну'],
  [/^\/oee$/, '/', 'На головну'],
  [/^\/costing$/, '/', 'На головну'],
  [/^\/finance$/, '/', 'На головну'],
  [/^\/kits-log$/, '/production', 'До виробництва'],
  [/^\/positions-log$/, '/production', 'До виробництва'],
];

const HUB_TO_HOME = new Set([
  '/stock',
  '/purchases',
  '/customer-orders',
  '/tasks',
  '/production',
]);

/**
 * @returns {{ show: boolean, to?: string, label?: string }}
 */
export function resolvePageBack(pathname, search = '', state = null) {
  if (state?.returnTo) {
    return {
      show: true,
      to: state.returnTo,
      label: state.returnLabel || 'Назад',
    };
  }

  const path = (pathname || '/').replace(/\/$/, '') || '/';
  if (path === '/' || path === '/login') {
    return { show: false };
  }

  for (const [re, to, label] of PARENT_RULES) {
    if (re.test(path)) {
      return { show: true, to, label };
    }
  }

  if (HUB_TO_HOME.has(path)) {
    return { show: true, to: '/', label: 'На головну' };
  }

  return { show: true, to: '/', label: 'Назад' };
}
